import { init } from '@eppo/js-client-sdk';
import { useEffect, useState } from 'react';

import { EppoContext } from './helpers';
import { trackEvent } from '../analytics/analytics';
import { EPPO_RANDOMISED_ASSIGNMENT_EVENT_NAME } from '../analytics/constants';
import { useAnalytics } from '../analytics/helpers';

import type { ReactElement } from 'react';

const NEXT_PUBLIC_EPPO_API_KEY = process.env.NEXT_PUBLIC_EPPO_API_KEY as string;
const IS_LHCI_TESTING = process.env.LHCI_TESTING;

interface IEppoRandomizationProvider {
  children: ReactElement;
}

export const EppoProvider = ({ children }: IEppoRandomizationProvider): ReactElement | null => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEppoReady, setIsEppoReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const { anonymousId } = useAnalytics();

  /**
   * Initialize Eppo with the provided API key
   * If the initialization is successful, set isEppoReady to true
   */
  useEffect(() => {
    init({
      apiKey: NEXT_PUBLIC_EPPO_API_KEY,
      assignmentLogger: {
        logAssignment(assignment) {
          trackEvent(EPPO_RANDOMISED_ASSIGNMENT_EVENT_NAME, { ...assignment });
        },
      },
    })
      .then(() => setIsEppoReady(true))
      .catch(() => {
        setIsError(true);
        console.warn('Failed to initialize Eppo');
      });
  }, []);

  /**
   * If Eppo and Analytics are ready, set isInitialized to true
   * This will allow Eppo to get the assignment for the user
   */
  useEffect(() => {
    if (isEppoReady && (!!anonymousId || IS_LHCI_TESTING)) {
      setIsInitialized(true);
    }
  }, [isEppoReady, anonymousId]);

  return <EppoContext.Provider value={{ isInitialized, isError }}>{children}</EppoContext.Provider>;
};
